import { createContext, useContext, useState, useEffect } from "react";
import { HELP_TOPICS } from "../constants/helpContent";
import { useAuth } from "./auth";
import { api } from "./api";

const HelpContext = createContext();

export function HelpProvider({ children }) {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [activeTopic, setActiveTopic] = useState("noi");
  const [feedback, setFeedback] = useState({});
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);

  const { authed, user } = useAuth() || {};

  // Load feedback from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("lapen_help_feedback");
      if (saved) setFeedback(JSON.parse(saved));
    } catch {}
  }, []);

  // Check if tour should auto-start strictly on user's first login
  useEffect(() => {
    // Only evaluate after user is successfully authenticated
    if (!authed || !user?.id) return;

    // 1. User record from DB already marked tour as seen
    if (user.has_seen_tour) return;

    // 2. User has logged in more than once (only trigger on their very first login)
    if (user.login_count > 1) return;

    // 3. Check localStorage flags (both per-user and global backup)
    try {
      const userTourKey = `lapen_tour_completed_${user.id}`;
      if (
        localStorage.getItem(userTourKey) === "true" ||
        localStorage.getItem("lapen_tour_completed") === "true"
      ) {
        return;
      }
    } catch {}

    // Delay slightly for smooth page mount on their first login
    const timer = setTimeout(() => {
      setIsTourOpen(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, [authed, user?.id, user?.has_seen_tour, user?.login_count]);

  // Tuck away bottom navigation when help panel or tour is active
  useEffect(() => {
    if (isHelpOpen || isTourOpen) {
      document.body.classList.add("has-modal-open");
    } else {
      document.body.classList.remove("has-modal-open");
    }
    return () => {
      document.body.classList.remove("has-modal-open");
    };
  }, [isHelpOpen, isTourOpen]);

  const openHelp = (topicKey) => {
    if (topicKey && HELP_TOPICS[topicKey]) {
      setActiveTopic(topicKey);
    } else if (!topicKey) {
      setActiveTopic("noi");
    }
    setIsHelpOpen(true);
  };

  const closeHelp = () => {
    setIsHelpOpen(false);
  };

  const submitFeedback = (topicKey, isHelpful) => {
    const updated = { ...feedback, [topicKey]: isHelpful ? "helpful" : "unhelpful" };
    setFeedback(updated);
    try {
      localStorage.setItem("lapen_help_feedback", JSON.stringify(updated));
    } catch {}
  };

  const startTour = () => {
    setTourStepIndex(0);
    setIsTourOpen(true);
  };

  const closeTour = () => {
    setIsTourOpen(false);
    setTourStepIndex(0);
    try {
      localStorage.setItem("lapen_tour_completed", "true");
      if (user?.id) {
        localStorage.setItem(`lapen_tour_completed_${user.id}`, "true");
      }
    } catch {}

    if (authed && user?.id) {
      api.post("/auth/tour-complete").catch(() => {});
      if (user) {
        user.has_seen_tour = true;
      }
    }
  };

  return (
    <HelpContext.Provider
      value={{
        isHelpOpen,
        activeTopic,
        topicData: HELP_TOPICS[activeTopic] || HELP_TOPICS["noi"],
        openHelp,
        closeHelp,
        feedback,
        submitFeedback,
        isTourOpen,
        tourStepIndex,
        setTourStepIndex,
        startTour,
        closeTour,
      }}
    >

      {children}
    </HelpContext.Provider>
  );
}

export function useHelp() {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error("useHelp must be used within a HelpProvider");
  }
  return context;
}
